import { from, Observable, timer, of } from "rxjs";
import { option } from "fp-ts";
import { tap, debounce, filter, concatMap, mergeMap, map, switchMap } from "rxjs/operators";
import { formOperation } from "../../wrappers/formOperation";
import * as fetchWrapper from "../../wrappers/fetch";
import * as PartyResponse1 from "../../../../domain/models/PartyResponse1";
import * as FirstPartyFetchResponse from "../../../../domain/models/FirstPartyFetchResponse";
import * as util from "../../util";
import { createChangeRouteAction } from "../router/createChangeRouteAction";
import { TActionObservable } from "../applyActions";
import { TGetState } from "../TGetState";
import { TSetState } from "../TSetState";
import { TDispatch } from "../TDispatch";
import { TParty1Editable } from "../../../../domain/codecs/Party";
import { TListingSellerUserDeleteFormNewDefault } from "../../../../domain/codecs/form/ListingSellerUserDeleteForm";
import { TListingSellerSuggestedSimilarUserForm, TListingSellerUserEditForm } from "../../../../domain/codecs/form/ListingSellerUserEditForm";
import { TPhoneNumberForm, TPhoneNumberFormNewDefault } from "../../../../domain/codecs/form/PhoneNumberForm";
import { TAddPartyListingForm } from "../../../../domain/codecs/form/AddPartyListingForm";
import { doFormValuesMatch } from "../../../../shared/src/codecs/types/formEditable";
import { TActionsDefinitionsList } from "./TAction";
import { action, routeAction } from "./actionFunctions";

export const actions: TActionsDefinitionsList = [
    {
        type: "CRM_ADD_NEW_PARTY_BUTTON_CLICKED" as const,
        run: (
            obs$: TActionObservable<"CRM_ADD_NEW_PARTY_BUTTON_CLICKED", undefined>,
            _getState: TGetState,
            _setState: TSetState,
            dispatch: TDispatch,
        ): void => {
            obs$.pipe(
                mergeMap(() =>
                    fetchWrapper.json<PartyResponse1.T>({
                        requestParams: {
                            url: `${env.REACT_APP_API_URL}/v1/parties`,
                            method: "POST",
                            body: option.none,
                        },
                        expectedTypeCodec: PartyResponse1.codec,
                        defaultResponse: PartyResponse1.newDefault(),
                    })(),
                ),
                tap(util.defaultCRMRequestErrorHandler),
                tap((response) => {
                    if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                        dispatch(createChangeRouteAction("VIEW_CRM_PARTY", { partyId: response.response.data.id }, {}));
                    }
                }),
            ).subscribe();
        },
    },
    routeAction("VIEW_CRM_PARTY", (obs$, lens, setState, getState) => {
        obs$.pipe(
            tap((a) => setState(lens.party_page.data.input.party_id.set(a.params.partyId))),
            mergeMap(() => formOperation("GetPartyData", lens.party_page.data.get()(getState()))),
            tap((response) => setState(lens.party_page.data.set(response))),
        ).subscribe();
    }),
    action("PARTY_PAGE_FORM_CHANGE", (obs$: Observable<TParty1Editable>, lens, setState, getState) => {
        obs$.pipe(
            tap((payload) => setState(lens.party_page.data.output.form.set(payload))),
            debounce(() => timer(1000)),
            tap(() => setState(lens.party_page.data.output.form.status.set("submitting"))),
            switchMap(() => formOperation("UpdateParty", lens.party_page.data.output.form.get()(getState()))),
            tap((response) => setState(lens.party_page.data.output.form.set(response))),
        ).subscribe();
    }),
    action("PARTY_PAGE_ADD_MEMBER", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            tap(() => setState(lens.party_page.data.output.member_create.status.set("submitting"))),
            mergeMap(() => formOperation("PartyMemberCreate", lens.party_page.data.output.member_create.get()(getState()))),
            mergeMap(() => formOperation("GetPartyData", lens.party_page.data.get()(getState()))),
            tap((response) => setState(lens.party_page.data.set(response))),
        ).subscribe();
    }),
    action("PARTY_PAGE_DELETE_MEMBER", (obs$: Observable<TListingSellerUserDeleteFormNewDefault>, lens, setState, getState) => {
        obs$.pipe(
            tap((deleteForm) => setState(lens.party_page.data.output.members.where((user) => user.original.id === deleteForm.original.id).children.delete_form.status.set("submitting"))),
            mergeMap((deleteForm) => formOperation("ListingSellerUserDelete", deleteForm)),
            mergeMap(() => formOperation("GetPartyData", lens.party_page.data.get()(getState()))),
            tap((response) => setState(lens.party_page.data.set(response))),
        ).subscribe();
    }),
    action("PARTY_PAGE_UPDATE_MEMBER", (obs$: Observable<Array<TListingSellerUserEditForm>>, lens, setState) => {
        obs$.pipe(
            tap((users) => setState(lens.party_page.data.output.members.set(users))),
            debounce(() => timer(1000)),
            concatMap((users) => users),
            filter((user) => user.status === "requiresSubmission"),
            tap((user) => setState(lens.party_page.data.output.members.where(doFormValuesMatch(user, "id")).status.set("submitting"))),
            mergeMap((user) => formOperation("ListingSellerUserEdit", user)),
            tap((user) => setState(lens.party_page.data.output.members.where(doFormValuesMatch(user, "id")).set(user))),
        ).subscribe();
    }),
    action("PARTY_PAGE_SWAP_MEMBER", (obs$: Observable<TListingSellerSuggestedSimilarUserForm>, lens, setState, getState) => {
        obs$.pipe(
            tap((payload) => setState(
                lens.party_page.data.output.members
                    .where((members) => members.edited.id === payload.edited.swap_from_user_id)
                    .children.suggested_similar_users
                    .where((suggested) => suggested.edited.swap_to_user_id === payload.edited.swap_to_user_id)
                    .status.set("submitting")
            )),
            switchMap((payload) => formOperation(
                "ListingSwapSellerPartyMember",
                lens.party_page.data.output.members
                    .where((members) => members.edited.id === payload.edited.swap_from_user_id)
                    .children.suggested_similar_users
                    .where((suggested) => suggested.edited.swap_to_user_id === payload.edited.swap_to_user_id)
                    .get()(getState())
            )),
            switchMap((response) =>
                response.status === "success"
                    ? from(formOperation("GetPartyData", lens.party_page.data.get()(getState())))
                        .pipe(tap((r) => setState(lens.party_page.data.set(r))))
                    : of(undefined)
            ),
        ).subscribe();
    }),
    action("PARTY_PAGE_UPDATE_MEMBER_PHONE_NUMBER", (obs$: Observable<Array<TPhoneNumberFormNewDefault>>, lens, setState) => {
        obs$.pipe(
            tap((phoneNumbers) => phoneNumbers.map((phoneNumber) => setState(lens.party_page.data.output.members.where((user) => user.edited.id === phoneNumber.edited.user_id).children.phone_numbers.where((pn) => pn.edited.id === phoneNumber.edited.id).set(phoneNumber)))),
            debounce(() => timer(1000)),
            concatMap((phoneNumbers) => phoneNumbers),
            filter((phoneNumber) => phoneNumber.status === "requiresSubmission"),
            tap((phoneNumber) => setState(lens.party_page.data.output.members.where((user) => user.edited.id === phoneNumber.edited.user_id).children.phone_numbers.where((pn) => pn.edited.id === phoneNumber.edited.id).status.set("submitting"))),
            mergeMap((phoneNumber) => formOperation("UpdateUserNumber", phoneNumber)),
            tap((phoneNumber) => setState(lens.party_page.data.output.members.where((user) => user.edited.id === phoneNumber.edited.user_id).children.phone_numbers.where((pn) => pn.edited.id === phoneNumber.edited.id).set(phoneNumber))),
        ).subscribe();
    }),
    action("PARTY_PAGE_ADD_PHONE_NUMBER_TO_MEMBER", (obs$: Observable<string>, lens, setState, getState) => {
        obs$.pipe(
            tap((userId) => setState(lens.party_page.data.output.member_phone_number_create.input.user_id.set(userId))),
            tap(() => setState(lens.party_page.data.output.member_phone_number_create.status.set("submitting"))),
            map(() => lens.party_page.data.output.member_phone_number_create.get()(getState())),
            mergeMap((ioForm) => formOperation("CreateUserNumber", ioForm)),
            mergeMap(() => formOperation("GetPartyData", lens.party_page.data.get()(getState()))),
            tap((response) => setState(lens.party_page.data.set(response))),
        ).subscribe();
    }),
    action("PARTY_PAGE_REMOVE_PHONE_NUMBER_FROM_MEMBER", (obs$: Observable<TPhoneNumberForm>, lens, setState, getState) => {
        obs$.pipe(
            tap((numberEditable) => setState(lens.party_page.data.output.member_phone_number_delete.input.set({
                user_id: numberEditable.original.user_id,
                number_id: numberEditable.original.id,
            }))),
            tap(() => setState(lens.party_page.data.output.member_phone_number_delete.status.set("submitting"))),
            map(() => lens.party_page.data.output.member_phone_number_delete.get()(getState())),
            mergeMap((ioForm) => formOperation("RemoveUserNumber", ioForm)),
            mergeMap(() => formOperation("GetPartyData", lens.party_page.data.get()(getState()))),
            tap((response) => setState(lens.party_page.data.set(response))),
        ).subscribe();
    }),
    action("PARTY_PAGE_ADD_LISTING_FORM_CHANGE", (obs$: Observable<TAddPartyListingForm>, lens, setState) => {
        obs$.pipe(
            tap((payload) => setState(lens.party_page.data.output.add_listing.set(payload))),
        ).subscribe();
    }),
    action("PARTY_PAGE_ADD_LISTING_SUBMIT", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            tap(() => setState(lens.party_page.data.output.add_listing.status.set("submitting"))),
            switchMap(() => formOperation("AddPartyListing", lens.party_page.data.output.add_listing.get()(getState()))),
            tap((response) => setState(lens.party_page.data.output.add_listing.set(response))),
            filter((response) => response.status === "success"),
            mergeMap(() => formOperation("GetPartyData", lens.party_page.data.get()(getState()))),
            tap((response) => setState(lens.party_page.data.set(response))),
        ).subscribe();
    }),
];
